import {
    DollarOutlined,
    ExportOutlined,
    ReloadOutlined,
    ShopOutlined,
    UserAddOutlined,
} from '@ant-design/icons';
import {
    Button,
    InputNumber,
    message,
    Modal,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid from '../../webgrid/src';
import PhysicalInventoryPanel from '../components/PhysicalInventoryPanel';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useInvoicePreview } from '../hooks/useInvoicePreview';
import {
    buildContractLabel,
    buildSaleModalItems,
    buildSellItemsFromModal,
    canSellItem,
    canSellSelection,
    formatLei,
    formatSaleDate,
    type GoodsSaleItem,
    normaliseMarkup,
    type SaleModalItem,
} from '../domain/goodsSale';
import { createTextColumn } from '../domain/gridColumns';
import useRequest from '../hooks/useRequest';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';

const { Title, Text } = Typography;

interface MarkupResponse {
    success: boolean;
    data: { markup: number };
}

interface ItemsResponse {
    success: boolean;
    data: { items: GoodsSaleItem[] };
}

interface SellResponse {
    success: boolean;
    data: { invoiceNumber: string; total: number; soldCount: number; buyerName: string };
}

interface ClientOption {
    ClNr: number;
    Nume: string;
    CNP?: string | null;
    Str?: string | null;
    Nr?: string | null;
    Oras?: string | null;
    Telefon?: string | null;
    NrContract?: string | null;
    IsDisabled?: boolean;
    [key: string]: unknown;
}

interface ClientsResponse {
    clients: ClientOption[];
    total: number;
}

// Let the tab grids stretch to fill the viewport down to near the bottom of the
// page. The offset accounts for the page header, tab bar and per-tab toolbar.
const TAB_GRID_HEIGHT = 'calc(100vh - 260px)';

const isAdminRole = (role: string | undefined): boolean => {
    const normalised = role?.toLowerCase();
    return normalised === 'admin' || normalised === 'administrator';
};

const GoodsSale = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const { cashPointId } = useCashDrawer();
    const { ensureCanBook } = useCashBookingReadiness();

    const [markup, setMarkup] = useState<number>(30);
    const [includePreciousMetals, setIncludePreciousMetals] = useState(false);
    const [items, setItems] = useState<GoodsSaleItem[]>([]);
    const [repostItems, setRepostItems] = useState<GoodsSaleItem[]>([]);
    const [selectedRows, setSelectedRows] = useState<GoodsSaleItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('sale');

    // Handover modal state
    const [handoverContract, setHandoverContract] = useState<GoodsSaleItem | null>(null);
    const [handoverClients, setHandoverClients] = useState<ClientOption[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

    // Sell modal state
    const [sellModalOpen, setSellModalOpen] = useState(false);
    const [sellStep, setSellStep] = useState<'buyer' | 'prices'>('buyer');
    const [saleItems, setSaleItems] = useState<SaleModalItem[]>([]);
    const [contractNumberForSale, setContractNumberForSale] = useState<string | null>(null);
    const [buyerClients, setBuyerClients] = useState<ClientOption[]>([]);
    const [buyerLoading, setBuyerLoading] = useState(false);
    const [selectedBuyer, setSelectedBuyer] = useState<ClientOption | null>(null);
    const [sellLoading, setSellLoading] = useState(false);

    // Invoice preview modal state
    const {
        invoicePreview,
        invoicePreviewLoading,
        invoicePreviewError,
        showInvoicePreview,
        closeInvoicePreview,
    } = useInvoicePreview(t);

    const handleSalePriceChange = useCallback((id: number, value: number | null) => {
        setSaleItems(prev =>
            prev.map(item => (item.id === id ? { ...item, salePrice: value ?? 0 } : item)),
        );
    }, []);

    const loadMarkup = useCallback(async () => {
        try {
            const result = await request<MarkupResponse>({ path: '/goods-sale/markup' });
            setMarkup(result.data.markup);
        } catch (error) {
            console.error('Failed to load markup', error);
        }
    }, [request]);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const result = await request<ItemsResponse>({
                path: '/goods-sale/items',
                params: { includePreciousMetals },
            });
            setItems(result.data.items);
            setSelectedRows([]);
        } catch (error) {
            message.error(t('goodsSale.loadError'));
            console.error('Failed to load goods', error);
        } finally {
            setLoading(false);
        }
    }, [request, includePreciousMetals, t]);

    const loadRepostItems = useCallback(async () => {
        if (!isAdmin) {
            return;
        }
        setLoading(true);
        try {
            const result = await request<ItemsResponse>({
                path: '/goods-sale/repost',
                params: { includePreciousMetals },
            });
            setRepostItems(result.data.items);
        } catch (error) {
            message.error(t('goodsSale.loadError'));
            console.error('Failed to load repost items', error);
        } finally {
            setLoading(false);
        }
    }, [request, includePreciousMetals, isAdmin, t]);

    useEffect(() => {
        loadMarkup();
    }, [loadMarkup]);

    useEffect(() => {
        if (activeTab === 'sale') {
            loadItems();
        } else if (activeTab === 'repost') {
            loadRepostItems();
        }
    }, [activeTab, loadItems, loadRepostItems]);

    const commitMarkup = useCallback(
        async (value: number | null) => {
            const normalised = normaliseMarkup(value);
            setMarkup(normalised);
            try {
                await request({
                    path: '/goods-sale/markup',
                    method: 'PUT',
                    params: { markup: normalised },
                });
                message.success(t('goodsSale.markupSaved'));
                loadItems();
                if (activeTab === 'repost') {
                    loadRepostItems();
                }
            } catch (error) {
                message.error(t('goodsSale.markupError'));
                console.error('Failed to save markup', error);
            }
        },
        [request, t, loadItems, loadRepostItems, activeTab],
    );

    const loadBuyers = useCallback(async () => {
        setBuyerLoading(true);
        try {
            const result = await request<ClientsResponse>({
                path: '/clients',
                params: { limit: 100000 },
            });
            setBuyerClients((result.clients ?? []).filter(client => !client.IsDisabled));
        } catch (error) {
            console.error('Failed to load clients', error);
            setBuyerClients([]);
        } finally {
            setBuyerLoading(false);
        }
    }, [request]);

    const openSellModal = useCallback(async () => {
        if (selectedRows.length === 0) {
            message.warning(t('goodsSale.nothingSelected'));
            return;
        }
        // The sale's money enters this drawer: a station that cannot book it is told so before
        // the buyer and the prices are entered, not when the sale is confirmed.
        const refusalKey = await ensureCanBook();
        if (refusalKey) {
            message.warning(t(refusalKey));
            return;
        }
        const contractLabel = buildContractLabel(selectedRows);
        setSaleItems(buildSaleModalItems(selectedRows));
        setContractNumberForSale(contractLabel);
        setSellStep('buyer');
        setBuyerClients([]);
        setSelectedBuyer(null);
        setSellModalOpen(true);
        void loadBuyers();
    }, [selectedRows, t, loadBuyers, ensureCanBook]);

    const confirmSale = useCallback(async () => {
        if (!selectedBuyer) {
            message.warning(t('goodsSale.noBuyerSelected'));
            return;
        }
        // The sale's money enters this workstation's drawer, so the register has to
        // be known for the sale to be booked into its open session.
        if (cashPointId === null) {
            message.error(t('goodsSale.noCashPoint'));
            return;
        }
        setSellLoading(true);
        try {
            const result = await request<SellResponse>({
                path: '/goods-sale/sell',
                method: 'POST',
                params: {
                    clientId: selectedBuyer.ClNr,
                    items: buildSellItemsFromModal(saleItems),
                    cashPointId,
                },
            });
            setSellModalOpen(false);
            void showInvoicePreview(result.data.invoiceNumber, 'goodsSale.invoicePreviewError');
            loadItems();
        } catch (error) {
            message.error(t('goodsSale.sellError'));
            console.error('Failed to sell goods', error);
        } finally {
            setSellLoading(false);
        }
    }, [request, selectedBuyer, saleItems, t, loadItems, showInvoicePreview, cashPointId]);

    const handleRemoveFromStock = useCallback(async () => {
        if (selectedRows.length === 0) {
            message.warning(t('goodsSale.nothingSelected'));
            return;
        }
        const contractNumbers = Array.from(new Set(selectedRows.map(row => row.nrContract)));
        Modal.confirm({
            title: t('goodsSale.removeConfirmTitle'),
            content: t('goodsSale.removeConfirmContent', { count: contractNumbers.length }),
            okText: t('goodsSale.remove'),
            okType: 'danger',
            onOk: async () => {
                try {
                    for (const contractNr of contractNumbers) {
                        await request({
                            path: '/goods-sale/remove-stock',
                            method: 'POST',
                            params: { contractNr },
                        });
                    }
                    message.success(t('goodsSale.removeSuccess'));
                    loadItems();
                } catch (error) {
                    message.error(t('goodsSale.removeError'));
                    console.error('Failed to remove from stock', error);
                }
            },
        });
    }, [request, selectedRows, t, loadItems]);

    const openHandover = useCallback(
        async (row: GoodsSaleItem) => {
            setHandoverContract(row);
            setSelectedClientId(null);
            try {
                const result = await request<ClientsResponse>({
                    path: '/clients',
                    params: { limit: 1000 },
                });
                setHandoverClients(result.clients ?? []);
            } catch (error) {
                console.error('Failed to load clients', error);
                setHandoverClients([]);
            }
        },
        [request],
    );

    const confirmHandover = useCallback(async () => {
        if (!handoverContract || selectedClientId === null) {
            message.warning(t('goodsSale.selectClient'));
            return;
        }
        try {
            await request({
                path: '/goods-sale/handover',
                method: 'POST',
                params: { contractNr: handoverContract.nrContract, clientId: selectedClientId },
            });
            message.success(t('goodsSale.handoverSuccess'));
            setHandoverContract(null);
            loadItems();
        } catch (error) {
            message.error(t('goodsSale.handoverError'));
            console.error('Failed to hand over goods', error);
        }
    }, [request, handoverContract, selectedClientId, t, loadItems]);

    const handleRepost = useCallback(
        async (row: GoodsSaleItem) => {
            try {
                await request({
                    path: '/goods-sale/repost',
                    method: 'POST',
                    params: { contractNr: row.nrContract },
                });
                message.success(t('goodsSale.repostSuccess'));
                loadRepostItems();
            } catch (error) {
                message.error(t('goodsSale.repostError'));
                console.error('Failed to repost goods', error);
            }
        },
        [request, t, loadRepostItems],
    );

    const buyerColumns = useMemo(
        () => [
            { key: 'ClNr', title: t('goodsSale.clientId'), dataIndex: 'ClNr', sortable: true },
            { key: 'Nume', title: t('clients.name'), dataIndex: 'Nume', sortable: true },
            { key: 'CNP', title: 'CNP', dataIndex: 'CNP' },
            { key: 'Oras', title: t('clients.city'), dataIndex: 'Oras' },
            { key: 'Str', title: t('clients.street'), dataIndex: 'Str' },
            { key: 'Nr', title: 'Nr', dataIndex: 'Nr' },
            { key: 'Telefon', title: 'Telefon', dataIndex: 'Telefon' },
            { key: 'NrContract', title: t('contracts.number'), dataIndex: 'NrContract' },
        ],
        [t],
    );

    const saleColumns = useMemo(
        () => [
            createTextColumn<GoodsSaleItem>('contractNumber', t('goodsSale.contractNumber')),
            {
                key: 'data',
                title: t('goodsSale.date'),
                dataIndex: 'data',
                sortable: true,
                filterable: true,
                render: (value: unknown) => formatSaleDate(value as string | null),
            },
            createTextColumn<GoodsSaleItem>('denumire', t('goodsSale.description')),
            {
                key: 'sumainLei',
                title: t('goodsSale.paid'),
                dataIndex: 'sumainLei',
                sortable: true,
                render: (value: unknown) => formatLei(value as number),
            },
            {
                key: 'propus',
                title: t('goodsSale.proposed'),
                dataIndex: 'propus',
                sortable: true,
                render: (value: unknown) => formatLei(value as number),
            },
            {
                key: 'invFizic',
                title: t('goodsSale.physicalStock'),
                dataIndex: 'invFizic',
                filterable: true,
                getFilterValue: (row: GoodsSaleItem) =>
                    row.invFizic ? t('goodsSale.present') : t('goodsSale.missing'),
                render: (value: unknown) =>
                    value ? (
                        <Tag color="green">{t('goodsSale.present')}</Tag>
                    ) : (
                        <Tag color="red">{t('goodsSale.missing')}</Tag>
                    ),
            },
        ],
        [t],
    );

    const repostColumns = useMemo(
        () => [
            createTextColumn<GoodsSaleItem>('status', t('goodsSale.status')),
            createTextColumn<GoodsSaleItem>('contractNumber', t('goodsSale.contractNumber')),
            createTextColumn<GoodsSaleItem>('denumire', t('goodsSale.description')),
            {
                key: 'sumainLei',
                title: t('goodsSale.paid'),
                dataIndex: 'sumainLei',
                render: (value: unknown) => formatLei(value as number),
            },
            {
                key: 'propus',
                title: t('goodsSale.proposed'),
                dataIndex: 'propus',
                render: (value: unknown) => formatLei(value as number),
            },
            {
                key: 'actions',
                title: t('goodsSale.actions'),
                dataIndex: 'actions',
                render: (_: unknown, row: GoodsSaleItem) => (
                    <Button size="small" type="primary" onClick={() => handleRepost(row)}>
                        {t('goodsSale.putBackOnSale')}
                    </Button>
                ),
            },
        ],
        [t, handleRepost],
    );

    const saleTab = (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button
                    data-testid="goods-sale-sell-button"
                    type="primary"
                    icon={<DollarOutlined />}
                    disabled={!canSellSelection(selectedRows.length)}
                    onClick={() => void openSellModal()}
                >
                    {t('goodsSale.sell')}
                </Button>
                <Button
                    danger
                    icon={<ExportOutlined />}
                    disabled={selectedRows.length === 0}
                    onClick={handleRemoveFromStock}
                >
                    {t('goodsSale.removeFromStock')}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={loadItems}>
                    {t('goodsSale.refresh')}
                </Button>
                <span data-testid="goods-sale-count">
                    {t('goodsSale.itemCount', { count: items.length })}
                </span>
            </Space>
            <WebGrid<GoodsSaleItem>
                data={items}
                columns={saleColumns}
                loading={loading}
                pagination
                pageSize={20}
                filterBar
                selectable
                multiSelect
                isRowSelectable={canSellItem}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                onRowContextMenu={(event, row) => {
                    event.preventDefault();
                    openHandover(row);
                }}
                rowIdAccessor={row => row.id}
                height={TAB_GRID_HEIGHT}
                emptyMessage={t('goodsSale.noData')}
            />
        </div>
    );

    const repostTab = (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button icon={<ReloadOutlined />} onClick={loadRepostItems}>
                    {t('goodsSale.refresh')}
                </Button>
                <span>{t('goodsSale.itemCount', { count: repostItems.length })}</span>
            </Space>
            <WebGrid<GoodsSaleItem>
                data={repostItems}
                columns={repostColumns}
                loading={loading}
                pagination
                pageSize={20}
                filterBar
                rowIdAccessor={row => row.id}
                height={TAB_GRID_HEIGHT}
                emptyMessage={t('goodsSale.noData')}
            />
        </div>
    );

    // Mounted only while active so the stock-take is re-synced each time the tab is opened.
    const physicalInventoryTab =
        activeTab === 'physicalInventory' ? (
            <PhysicalInventoryPanel height={TAB_GRID_HEIGHT} />
        ) : null;

    const tabItems = [
        { key: 'sale', label: t('goodsSale.saleTab'), children: saleTab },
        ...(isAdmin
            ? [{ key: 'repost', label: t('goodsSale.repostTab'), children: repostTab }]
            : []),
        {
            key: 'physicalInventory',
            label: t('goodsSale.physicalInventoryTab'),
            children: physicalInventoryTab,
        },
    ];

    let invoicePreviewContent = (
        <Typography.Text type="danger">
            {invoicePreviewError ?? t('goodsSale.invoicePreviewError')}
        </Typography.Text>
    );

    if (invoicePreviewLoading) {
        invoicePreviewContent = (
            <div
                aria-label={t('goodsSale.invoicePreviewLoading')}
                style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}
            >
                <Spin size="large" />
            </div>
        );
    } else if (invoicePreview?.url) {
        invoicePreviewContent = (
            <iframe
                title={t('goodsSale.invoiceTitle', {
                    number: invoicePreview.invoiceNumber,
                })}
                src={invoicePreview.url}
                style={{ border: 0, height: '100%', width: '100%' }}
            />
        );
    }

    return (
        <div>
            <div
                className="page-header"
                style={{ ['--page-header-accent']: '#65a30d' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <ShopOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('goodsSale.title')}
                        </Title>
                        <Text type="secondary">{t('goodsSale.subtitle')}</Text>
                    </div>
                </div>
                <Space wrap className="page-header__actions">
                    <span>{t('goodsSale.markup')}</span>
                    <InputNumber
                        min={0}
                        value={markup}
                        addonAfter="%"
                        onChange={value => setMarkup(normaliseMarkup(value))}
                        onBlur={() => commitMarkup(markup)}
                        onPressEnter={() => commitMarkup(markup)}
                        aria-label={t('goodsSale.markup')}
                    />
                    <span>{t('goodsSale.showPreciousMetals')}</span>
                    <Switch
                        checked={includePreciousMetals}
                        onChange={setIncludePreciousMetals}
                        aria-label={t('goodsSale.showPreciousMetals')}
                    />
                </Space>
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

            <Modal
                title={t('goodsSale.handoverTitle')}
                open={handoverContract !== null}
                onOk={confirmHandover}
                onCancel={() => setHandoverContract(null)}
                okText={t('goodsSale.handover')}
            >
                <p>
                    {t('goodsSale.handoverContract', {
                        contract: handoverContract?.contractNumber ?? handoverContract?.nrContract,
                    })}
                </p>
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder={t('goodsSale.selectClient')}
                    value={selectedClientId ?? undefined}
                    onChange={setSelectedClientId}
                    optionFilterProp="label"
                    options={handoverClients.map(client => ({
                        value: client.ClNr,
                        label: client.Nume,
                    }))}
                />
            </Modal>

            {/* Multi-step sell modal */}
            <Modal
                title={
                    sellStep === 'buyer'
                        ? t('goodsSale.buyerSelectionTitle')
                        : t('goodsSale.priceEntryTitle', {
                              contract: contractNumberForSale,
                          })
                }
                open={sellModalOpen}
                onCancel={() => setSellModalOpen(false)}
                width={680}
                footer={
                    sellStep === 'buyer'
                        ? [
                              <Button key="cancel" onClick={() => setSellModalOpen(false)}>
                                  {t('goodsSale.cancel')}
                              </Button>,
                              <Button
                                  key="next"
                                  type="primary"
                                  disabled={!selectedBuyer}
                                  onClick={() => setSellStep('prices')}
                              >
                                  {t('goodsSale.next')}
                              </Button>,
                          ]
                        : [
                              <Button key="back" onClick={() => setSellStep('buyer')}>
                                  {t('goodsSale.back')}
                              </Button>,
                              <Button
                                  key="confirm"
                                  type="primary"
                                  loading={sellLoading}
                                  onClick={confirmSale}
                              >
                                  {t('goodsSale.confirmSale')}
                              </Button>,
                          ]
                }
            >
                {sellStep === 'buyer' && (
                    <div>
                        <Space style={{ marginBottom: 12 }} wrap>
                            <Button
                                type="link"
                                icon={<UserAddOutlined />}
                                onClick={() => window.open('/clients', '_blank')}
                            >
                                {t('goodsSale.registerNewClient')}
                            </Button>
                        </Space>
                        <div data-testid="sell-buyer-grid">
                            <WebGrid<ClientOption>
                                data={buyerClients}
                                columns={buyerColumns}
                                loading={buyerLoading}
                                pagination
                                pageSize={20}
                                filterBar
                                exportable={false}
                                selectable
                                multiSelect={false}
                                rowIdAccessor={client => client.ClNr}
                                selectedRows={buyerClients.filter(
                                    client => client.ClNr === selectedBuyer?.ClNr,
                                )}
                                onSelectionChange={rows => setSelectedBuyer(rows[0] ?? null)}
                                onRowClick={client => setSelectedBuyer(client)}
                                height="360px"
                                storageKey="goods-sale-buyer-grid-column-widths"
                                emptyMessage={t('clients.noData')}
                            />
                        </div>
                        {selectedBuyer && (
                            <div style={{ marginTop: 10 }}>
                                <strong>{t('goodsSale.buyer')}: </strong>
                                {selectedBuyer.Nume}
                            </div>
                        )}
                    </div>
                )}
                {sellStep === 'prices' && (
                    <div>
                        {selectedBuyer && (
                            <div style={{ marginBottom: 10 }}>
                                <strong>{t('goodsSale.buyer')}: </strong>
                                {selectedBuyer.Nume}
                            </div>
                        )}
                        <Table<SaleModalItem>
                            dataSource={saleItems}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: t('goodsSale.description'),
                                    dataIndex: 'denumire',
                                    ellipsis: true,
                                },
                                {
                                    title: t('goodsSale.paid'),
                                    dataIndex: 'sumainLei',
                                    width: 100,
                                    render: (v: number) => formatLei(v),
                                },
                                {
                                    title: t('goodsSale.salePrice'),
                                    dataIndex: 'salePrice',
                                    width: 140,
                                    render: (_: unknown, record: SaleModalItem) => (
                                        <InputNumber
                                            min={0}
                                            value={record.salePrice}
                                            addonAfter="LEI"
                                            style={{ width: 130 }}
                                            onChange={val => handleSalePriceChange(record.id, val)}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                )}
            </Modal>

            {/* Invoice preview modal */}
            <Modal
                title={t('goodsSale.invoiceTitle', { number: invoicePreview?.invoiceNumber })}
                open={invoicePreview !== null}
                onCancel={closeInvoicePreview}
                footer={[
                    <Button
                        key="print"
                        icon={<ExportOutlined />}
                        disabled={!invoicePreview?.url}
                        onClick={() => {
                            if (invoicePreview?.url) {
                                window.open(invoicePreview.url, '_blank');
                            }
                        }}
                    >
                        {t('goodsSale.print')}
                    </Button>,
                    <Button key="close" type="primary" onClick={closeInvoicePreview}>
                        {t('goodsSale.close')}
                    </Button>,
                ]}
                width="80%"
                styles={{ body: { height: '75vh' } }}
            >
                {invoicePreviewContent}
            </Modal>
        </div>
    );
};

export default GoodsSale;
